import { CSSProperties } from 'react'
import { CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import styled from '@emotion/styled'
import { Icon } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { format, formatDateTime } from '@cbgms/base/utils/datetime'

const headCellStyles: CSSProperties = {
  paddingLeft: '8px',
  paddingRight: '8px',
  paddingTop: '15px',
  paddingBottom: '15px',
  fontSize: '16px',
  fontWeight: 'bold',
  color: '#666666',
  whiteSpace: 'nowrap',
  borderBottom: '2px solid #ccc'
}
const bodyCellStyles: CSSProperties = {
  fontSize: '14px',
  paddingLeft: '8px',
  paddingRight: '8px',
  paddingTop: '15px',
  paddingBottom: '15px',
  color: '#666666'
}

const loaderStyles: CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: 'calc(90px + 50%)',
  transform: 'translate(-50%, -50%)'
}

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#f3f3f3'
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0
  }
}))

export interface IEmailAttachment {
  AttachmentDescription: string
  AttachmentUUID: string
  AttachmentName: string
  CreatedAt: string
  CreatedByName: string
  UpdatedAt: string
  UpdatedByName: string
  App_Id: string
}

export interface IList {
  list: IEmailAttachment[]
  isLoading: boolean
  setEditingAttachmentDialogOpen: (editingAttachment: IEmailAttachment) => void
  setDeletingAttachmentDialogOpen: (deletingAttachment: IEmailAttachment) => void
  downloadEmailAttachment: (attachmentUUID: string, fileName: string) => void
}

const ListAttachment = ({
  list,
  isLoading,
  setEditingAttachmentDialogOpen,
  setDeletingAttachmentDialogOpen,
  downloadEmailAttachment
}: IList) => {
  const { t } = useTranslation()

  const handleOnEditClick = (attachmentData: IEmailAttachment) => {
    setEditingAttachmentDialogOpen(attachmentData)
  }

  const handleOnDeleteClick = (attachmentData: IEmailAttachment) => {
    setDeletingAttachmentDialogOpen(attachmentData)
  }

  const handleOnDownloadClick = (attachmentUUID: string, fileName: string) => {
    downloadEmailAttachment(attachmentUUID, fileName)
  }

  return (
    <TableContainer component={Paper} sx={{ minHeight: isLoading ? 400 : 'auto', maxHeight: 400 }}>
      {isLoading ? (
        <CircularProgress sx={loaderStyles} color='info' />
      ) : (
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellStyles}>{t('email_attachment.email_attch_desc')}</TableCell>
              <TableCell sx={headCellStyles}>{t('email_attachment.email_attch_filename')}</TableCell>
              <TableCell sx={headCellStyles}>{t('email_attachment.email_attch_creation')}</TableCell>
              <TableCell sx={headCellStyles}>{t('email_attachment.email_attch_actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map(attachment => (
              <StyledTableRow key={attachment.AttachmentUUID}>
                <TableCell sx={bodyCellStyles}>{attachment.AttachmentDescription}</TableCell>
                <TableCell sx={bodyCellStyles}>{attachment.AttachmentName}</TableCell>
                <TableCell sx={bodyCellStyles}>{formatDateTime(attachment.UpdatedAt, format.shortDateFull)}</TableCell>
                <TableCell sx={bodyCellStyles}>
                  <Icon
                    name='download'
                    color='blue'
                    className='basic-table-action-icons'
                    onClick={() => handleOnDownloadClick(attachment.AttachmentUUID, attachment.AttachmentName)}
                    data-testid='btn-download-attachment'
                  />
                  <Icon
                    name='edit'
                    color='blue'
                    className='basic-table-action-icons'
                    onClick={() => handleOnEditClick(attachment)}
                    data-testid='btn-edit-attachment'
                  />
                  <Icon
                    name='delete'
                    color='red'
                    className='basic-table-action-icons'
                    onClick={() => handleOnDeleteClick(attachment)}
                    data-testid='btn-delete-attachment'
                  />
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  )
}

export default ListAttachment
